import { useGetStudy } from "@/api/studio/studies/queries";
import { TextSkeleton } from "@/features/projects/components/text-skeleton";
import { CheckCircleIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import { dynamicUseCase } from "@/utils/dynamic-use-case";
import { buildStepRoute, getFlowSteps, isStepCompleted, StudyStep, StudyType } from "@/features/studies/utils/study-flow.config";
import { useLocation } from "wouter";
import { FolderIcon } from "@heroicons/react/24/solid";

/* Prop Types */
interface StudyFlowStepsProps {
  projectId: string;
  studyId: string;
  currentStep: string;
  // Optional hook run before navigating to a step (e.g. to save in-progress work)
  onBeforeStepNavigate?: () => Promise<void> | void;
}

/*
 * StudyFlowSteps component
 * Displays each step of the study flow and if it is completed
 * Now dynamically renders steps based on study type configuration
 */
export const StudyFlowSteps = ({ projectId, studyId, currentStep, onBeforeStepNavigate }: StudyFlowStepsProps) => {
  /* Queries */
  const { data: studyDetails, isLoading: isStudyDetailsLoading } = useGetStudy(projectId, studyId);

  /* Hooks */
  const [, navigate] = useLocation();

  // Get the flow steps for this study type
  const studyType: StudyType = studyDetails?.type || "asset";
  const flowSteps = getFlowSteps(studyType);

  // Filter out intermediate steps (study-process and study-upload)
  const visibleSteps = flowSteps.filter((step) => step.id !== "study-process" && step.id !== "study-upload");

  /* Render */
  return (
    <div className="flex items-center">
      <div className="flex items-center px-4 mr-1">
        {isStudyDetailsLoading && (
          <>
            <div className="skeleton w-5 h-5 rounded-lg mr-2" />
            <TextSkeleton variant="h3" className="w-24" />
          </>
        )}
        {/* Back to Projects */}
        <div
          role="button"
          aria-label="Back to projects"
          onClick={async () => {
            // Give the current step a chance to persist its work before we leave it
            await onBeforeStepNavigate?.();
            navigate(`/projects/${projectId}`);
          }}
          className="flex gap-4 ml-2 cursor-pointer hover:opacity-70"
        >
          <FolderIcon className="size-5" />
          <ChevronRightIcon className="size-5" />
        </div>

        {/* Use case name */}
        {studyDetails ? dynamicUseCase(studyDetails?.useCase.name, "size-5 ml-4 mr-2") : null}
        <h3 className="max-w-56 truncate">{studyDetails?.name}</h3>
      </div>

      {/* Dynamically render flow steps based on study type */}
      {visibleSteps.map((step) => {
        const isCompleted = isStepCompleted(studyType, currentStep as StudyStep, step.id);
        const isClickable = isCompleted;

        return (
          <div key={step.id} className="flex items-center">
            <ChevronRightIcon className="size-5" />
            <div
              role={isClickable ? "button" : undefined}
              onClick={
                isClickable
                  ? async () => {
                      // Give the current step a chance to persist its work before we leave it
                      await onBeforeStepNavigate?.();
                      navigate(buildStepRoute(projectId, studyId, studyType, step));
                    }
                  : undefined
              }
              className={`flex items-center justify-center p-3 ml-1 ${currentStep === step.id ? "border-b-2" : ""} ${isClickable ? "cursor-pointer hover:opacity-70" : ""}`}
            >
              <p className="text-sm mr-2">{step.label}</p>
              {isCompleted && <CheckCircleIcon className="size-3.5 text-success" />}
            </div>
          </div>
        );
      })}
    </div>
  );
};
